import { z } from 'zod';

/**
 * Identity of the GitHub App install real-time push. The server emits it
 * via `ctx.services.broadcastService.broadcastPluginEvent(GITHUB_PLUGIN_NAME,
 * GITHUB_INSTALLATION_SYNCED_EVENT, payload, orgId)` and the web consumes it
 * with `usePluginEvent(GITHUB_PLUGIN_NAME, GITHUB_INSTALLATION_SYNCED_EVENT,
 * ...)`. Kept as shared constants so the two ends can never drift.
 */
export const GITHUB_PLUGIN_NAME = 'github';
export const GITHUB_INSTALLATION_SYNCED_EVENT = 'installation_synced';

/**
 * Payload broadcast to the active org each time a GitHub App installation is
 * recorded. Two server paths emit it:
 *
 * - the setup callback, the moment the in-app install/reconfigure flow binds
 *   the installation to the org (`webhookDelivered` reflects whether GitHub's
 *   webhook had already created the row by then); and
 * - the `installation` webhook receiver, which always sets
 *   `webhookDelivered: true` — that event IS the proof GitHub is delivering
 *   webhooks to this deployment end-to-end.
 *
 * The web waits on these to turn the "Install on a GitHub Organization" click
 * into a live confirmation without the user refreshing.
 */
export const GitHubInstallationSyncedSchema = z.object({
  installationId: z.number().int().positive(),
  accountLogin: z.string(),
  accountType: z.enum(['User', 'Organization']),
  repositorySelection: z.enum(['all', 'selected']),
  /**
   * True once GitHub's `installation` webhook has been received for this
   * install — i.e. events are flowing to Reflex, not merely that the in-app
   * callback wrote the row.
   */
  webhookDelivered: z.boolean(),
});

export type GitHubInstallationSynced = z.infer<typeof GitHubInstallationSyncedSchema>;
